// /frontend/src/plugins/vue-router.ts

/**
 * Vue Router plugin setup.
 * This file configures the router instance, defines all application routes,
 * and sets up global navigation guards for authentication.
 *
 * @author Alexander Funke
 * @lastChange 2025-09-18
 */

import { createRouter, createWebHistory } from "vue-router";
import { useAuthStore } from '@/stores/auth.store';

// --- Layout Components ---
import DefaultLayout from "@/layouts/DefaultLayout.vue";
import AuthLayout from "@/layouts/AuthLayout.vue";

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      // --- Protected Routes ---
      // All routes that require a user to be logged in.
      // They are rendered within the DefaultLayout (with sidebar and app bar).
      path: "/",
      component: DefaultLayout,
      meta: { requiresAuth: true },
      children: [
        {
          path: 'dashboard',
          component: () => import("@/views/dashboard.vue"),
        },
        {
          path: 'devices',
          children: [
            {
              path: '',
              component: () => import('@/views/devices.vue'),
            },
            {
              path: ':ID_devices/details',
              component: () => import('@/views/devices/details.vue'),
              props: true
            },
          ]
        },
        {
          path: "servers",
          component: () => import("@/views/servers.vue"),
        },
        {
          path: "companies",
          component: () => import("@/views/companies.vue"),
        },
        {
          path: "control-panel",
          component: () => import("@/views/control-panel.vue"),
        },
        {
          path: "settings",
          component: () => import("@/views/settings.vue"),
        },
        // A redirect for the root path ('/') to the dashboard.
        {
          path: '',
          redirect: '/dashboard'
        }
      ]
    },
    {
      // --- Public Authentication Routes ---
      // These routes are for users who are not logged in.
      // They are rendered within the simple AuthLayout.
      path: "/auth",
      component: AuthLayout,
      children: [
        {
          path: "signin",
          component: () => import("@/views/auth/signin.vue"),
        },
        {
          path: "forgot-password",
          component: () => import("@/views/auth/forgot-password.vue"),
        },
        {
          path: "reset-password",
          component: () => import("@/views/auth/reset-password.vue"),
        },
        // A redirect for the base '/auth' path to the sign-in page.
        {
          path: '',
          redirect: '/auth/signin'
        }
      ]
    },
    // --- Optional: Catch-all 404 Route ---
    // {
    //   path: '/:pathMatch(.*)*',
    //   name: 'NotFound',
    //   component: () => import('@/views/NotFound.vue')
    // }
  ]
});

/**
 * Global Navigation Guard.
 * This function is executed before each navigation to handle authentication checks.
 *
 * @author Alexander Funke
 * @lastChange 2025-09-15
 */
router.beforeEach((to, from, next) => {
  const authStore = useAuthStore();
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth);

  // Case 1: Route requires login, but user is not authenticated.
  if (requiresAuth && !authStore.isAuthenticated) {
    // Store the intended destination and redirect to the sign-in page.
    authStore.returnUrl = to.fullPath;
    return next({ path: '/auth/signin' });
  }

  // Case 2: User is authenticated and tries to access a public-only auth page.
  if (to.path.startsWith('/auth') && authStore.isAuthenticated) {
    // Redirect them to the dashboard.
    return next({ path: '/dashboard' });
  }

  // In all other cases, allow the navigation.
  next();
});

export default router;
